import React from 'react';
import { FormattedMessage } from 'react-intl';

/**
 * A group with no `titleId` is an ungrouped run.
 *
 * Figma pins the settings group to the rail's foot; not reproduced, because `PanelSection` sizes to its
 * content and leaves no free space for `margin-top: auto`. That fix is in the shared Panel height chain.
 */
export type RailGroup = { titleId?: string; keys: string[] };

export type GroupableItem = { key: string; shouldShow?: boolean; isLoading?: boolean };

// Cast: a title cannot be a full `T`. Both call sites dispatch on `isSectionTitle` first, so the
// fields it lacks are never read.
const sectionTitle = <T extends GroupableItem>(titleId: string, members: T[]): T =>
  ({
    key: titleId,
    title: React.createElement(FormattedMessage, { id: titleId }),
    isSectionTitle: true,
    // Whole groups legitimately vanish on permissions, so a label must not outlive its members.
    shouldShow: members.some(member => member.shouldShow !== false),
    isLoading: members.some(member => member.isLoading)
  }) as unknown as T;

export const applyRailGroups = <T extends GroupableItem>(items: T[], groups: RailGroup[]): T[] => {
  if (groups.length === 0) return items;

  const byKey = new Map(items.map(entry => [entry.key, entry]));
  const grouped: T[] = [];
  const claimed = new Set<string>();

  for (const group of groups) {
    const members = group.keys.map(key => byKey.get(key)).filter((entry): entry is T => !!entry);
    if (members.length === 0) continue;

    members.forEach(member => claimed.add(member.key));
    if (group.titleId) grouped.push(sectionTitle(group.titleId, members));
    grouped.push(...members);
  }

  // Anything the descriptor does not name keeps its original relative order and follows the groups.
  // Drifts is the live case: Figma omits it and the tech design says to keep it.
  return [...grouped, ...items.filter(entry => !claimed.has(entry.key))];
};
